import { useState } from 'react'

import { api } from '../api/client'
import type { EagleParam, EaglePunto, EagleSerie } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { formatConUnita } from './eagleTesti'
import { Spinner } from './common'
import { GraficoSerie } from './GraficoSerie'

/** Le finestre di tempo del grafico: le stesse ovunque in Eagle. */
export const FINESTRE: { ore: number; label: () => string }[] = [
  { ore: 0.25, label: () => t('15 minuti') },
  { ore: 0.5, label: () => t('30 minuti') },
  { ore: 1, label: () => t('1 ora') },
  { ore: 3, label: () => t('3 ore') },
  { ore: 6, label: () => t('6 ore') },
  { ore: 24, label: () => t('24 ore') },
  { ore: 168, label: () => t('7 giorni') },
]

/**
 * L'andamento di un parametro con la scelta della finestra: carica la serie
 * dal Control Plane e la passa al grafico. Mentre ricarica tiene il disegno
 * precedente, un po' sbiadito: niente salti.
 */
export function SerieParametro({
  ciId, param, principale, nomiSonde, oreIniziali = 24,
}: {
  ciId: string
  param: Pick<EagleParam, 'key' | 'unit' | 'warn' | 'crit' | 'direction' | 'name' | 'kind'>
  principale?: string | null
  nomiSonde?: Record<string, string>
  oreIniziali?: number
}) {
  const [ore, setOre] = useState(oreIniziali)
  const serie = useAsync(() => api.ciEagleSerie(ciId, param.key, ore), [ciId, param.key, ore])
  return (
    <div className="serie-parametro">
      <div className="segmented" role="tablist" aria-label={t('Finestra di tempo')} style={{ marginBottom: 8 }}>
        {FINESTRE.map((f) => (
          <button key={f.ore} type="button" role="tab" aria-selected={ore === f.ore}
                  className={ore === f.ore ? 'segmented__on' : ''} onClick={() => setOre(f.ore)}>
            {f.label()}
          </button>
        ))}
      </div>
      {serie.loading && !serie.data ? (
        <Spinner />
      ) : (
        <div style={{ opacity: serie.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {param.kind === 'interface' && serie.data && Object.keys(serie.data.derived ?? {}).length > 0 ? (
            <SerieInterfaccia serie={serie.data} param={param} ore={ore} principale={principale} nomiSonde={nomiSonde} />
          ) : (
            <>
              {param.kind === 'ping_fast' && serie.data && <RiepilogoPingRapido punti={serie.data.points} />}
              <GraficoSerie punti={serie.data?.points ?? []} param={param} ore={ore} principale={principale} nomiSonde={nomiSonde} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Un'interfaccia non è un numero solo. Accanto all'utilizzo (il verso più
 * carico, con le soglie) la sonda manda le serie **derivate**: il traffico in
 * entrata e in uscita in Mbit/s, gli errori e gli scarti nell'intervallo. Qui
 * diventano tre cose: i numeri dell'ultima lettura, il grafico del traffico
 * con i due versi (una linea ciascuno, con la legenda), quello di errori e
 * scarti — e sotto, l'utilizzo con le soglie come per ogni altro parametro.
 *
 * I due versi entrano in `GraficoSerie` come se fossero due punti di
 * osservazione: è esattamente ciò che sa disegnare — più linee, legenda,
 * lettura congiunta al passaggio del mouse. Con più sonde si tiene quella
 * principale (o quella che ha più letture): sommare i versi di due sonde
 * disegnerebbe il doppio del traffico.
 */
function SerieInterfaccia({ serie, param, ore, principale, nomiSonde }: {
  serie: EagleSerie
  param: Pick<EagleParam, 'key' | 'unit' | 'warn' | 'crit' | 'direction' | 'name' | 'kind'>
  ore: number
  principale?: string | null
  nomiSonde?: Record<string, string>
}) {
  const d = serie.derived ?? {}
  const sonda = sondaDiRiferimento([...(d.in ?? []), ...(d.out ?? [])], principale)
  const soloSonda = (punti: EaglePunto[] | undefined) => (punti ?? []).filter((p) => !sonda || !p.appliance_id || p.appliance_id === sonda)
  const conVerso = (punti: EaglePunto[] | undefined, verso: string) => soloSonda(punti).map((p) => ({ ...p, appliance_id: verso }))
  const traffico = [...conVerso(d.in, 'in'), ...conVerso(d.out, 'out')]
  const problemi = [...conVerso(d.errors, 'errors'), ...conVerso(d.discards, 'discards')]
  const pacchetti = [...conVerso(d.pkts_in, 'pkts_in'), ...conVerso(d.pkts_out, 'pkts_out')]
  const ultimo = (punti: EaglePunto[] | undefined) => {
    const lista = soloSonda(punti)
    return lista.length ? lista[lista.length - 1] : null
  }
  const somma = (punti: EaglePunto[] | undefined) => soloSonda(punti).reduce((s, p) => s + (p.value ?? 0) * Math.max(1, p.count ?? 1), 0)
  const uIn = ultimo(d.in)
  const uOut = ultimo(d.out)
  const uPktIn = ultimo(d.pkts_in)
  const uPktOut = ultimo(d.pkts_out)
  const pps = (v: number | null | undefined) => formatConUnita(v, 'pkt/s')
  const uUtil = serie.points.length ? serie.points[serie.points.length - 1] : null
  const mbit = (v: number | null | undefined) => formatConUnita(v, 'Mbit/s')
  const nomi = { in: t('Entrata'), out: t('Uscita'), errors: t('Errori'), discards: t('Scarti'), pkts_in: t('Pacchetti in entrata'), pkts_out: t('Pacchetti in uscita') }
  const finestra = FINESTRE.find((f) => f.ore === ore)?.label() ?? ''

  return (
    <div className="serie-interfaccia">
      <div className="serie-interfaccia__numeri">
        <div><span className="serie-interfaccia__etichetta">{t('Entrata')}</span><strong className="mono">{mbit(uIn?.value)}</strong>{uIn?.max != null && <span className="muted eagle-piccolo"> · {t('picco {v}', { v: mbit(uIn.max) })}</span>}</div>
        <div><span className="serie-interfaccia__etichetta">{t('Uscita')}</span><strong className="mono">{mbit(uOut?.value)}</strong>{uOut?.max != null && <span className="muted eagle-piccolo"> · {t('picco {v}', { v: mbit(uOut.max) })}</span>}</div>
        {(uPktIn || uPktOut) && (
          <div><span className="serie-interfaccia__etichetta">{t('Pacchetti')}</span><strong className="mono">{pps(uPktIn?.value)}</strong><span className="muted eagle-piccolo"> {t('in')} · </span><strong className="mono">{pps(uPktOut?.value)}</strong><span className="muted eagle-piccolo"> {t('out')}</span></div>
        )}
        {uUtil?.value != null && (
          <div><span className="serie-interfaccia__etichetta">{t('Utilizzo')}</span><strong className="mono">{uUtil.value.toLocaleString(localeCorrente(), { maximumFractionDigits: 2 })} {param.unit}</strong></div>
        )}
        <div><span className="serie-interfaccia__etichetta">{t('Errori')}</span><strong className={`mono${somma(d.errors) > 0 ? ' eagle-motivo' : ''}`}>{somma(d.errors).toLocaleString(localeCorrente())}</strong><span className="muted eagle-piccolo"> · {finestra}</span></div>
        <div><span className="serie-interfaccia__etichetta">{t('Scarti')}</span><strong className={`mono${somma(d.discards) > 0 ? ' eagle-motivo' : ''}`}>{somma(d.discards).toLocaleString(localeCorrente())}</strong><span className="muted eagle-piccolo"> · {finestra}</span></div>
      </div>

      <h5 className="serie-interfaccia__titolo">{t('Traffico (Mbit/s)')}</h5>
      <GraficoSerie punti={traffico} ore={ore} principale="in" nomiSonde={nomi} altezza={180}
                    param={{ unit: 'Mbit/s', warn: null, crit: null, direction: 'above', name: param.name, kind: 'interface' }} />

      {pacchetti.length > 0 && (
        <>
          <h5 className="serie-interfaccia__titolo">{t('Pacchetti al secondo')}</h5>
          <GraficoSerie punti={pacchetti} ore={ore} principale="pkts_in" nomiSonde={nomi} altezza={140}
                        param={{ unit: 'pkt/s', warn: null, crit: null, direction: 'above', name: param.name, kind: 'interface' }} />
        </>
      )}

      {problemi.length > 0 && (
        <>
          <h5 className="serie-interfaccia__titolo">{t('Errori e scarti nell’intervallo')}</h5>
          <GraficoSerie punti={problemi} ore={ore} principale="errors" nomiSonde={nomi} altezza={140}
                        param={{ unit: '', warn: null, crit: null, direction: 'above', name: param.name, kind: 'interface' }} />
        </>
      )}

      <h5 className="serie-interfaccia__titolo">{t('Utilizzo ({unita}), con le soglie', { unita: param.unit })}</h5>
      <GraficoSerie punti={serie.points} param={param} ore={ore} principale={principale} nomiSonde={nomiSonde} altezza={160} />
    </div>
  )
}

/**
 * The fast ping in numbers: the last minute (average, min–max, packets
 * lost) and the loss over the window. The chart below draws the average
 * with the min–max band — that band is the fluctuation.
 */
function RiepilogoPingRapido({ punti }: { punti: EaglePunto[] }) {
  const finestre = punti.filter((p) => p.count != null)
  const ultimo = finestre.length ? finestre[finestre.length - 1] : null
  const inviati = finestre.reduce((s, p) => s + (p.count ?? 0), 0)
  const persi = finestre.reduce((s, p) => s + (p.failed ?? 0), 0)
  if (!ultimo) return null
  const ms = (v: number | null | undefined) => formatConUnita(v, 'ms')
  return (
    <div className="serie-interfaccia__numeri" style={{ marginBottom: 8 }}>
      <div><span className="serie-interfaccia__etichetta">{t('Ultimo minuto')}</span><strong className="mono">{ms(ultimo.value)}</strong>
        {ultimo.min != null && ultimo.max != null && <span className="muted eagle-piccolo"> · {t('da {a} a {b}', { a: ms(ultimo.min), b: ms(ultimo.max) })}</span>}</div>
      <div><span className="serie-interfaccia__etichetta">{t('Persi')}</span>
        <strong className={`mono${(ultimo.failed ?? 0) > 0 ? ' eagle-motivo' : ''}`}>{t('{n} su {m}', { n: ultimo.failed ?? 0, m: ultimo.count ?? 0 })}</strong></div>
      {inviati > 0 && (
        <div><span className="serie-interfaccia__etichetta">{t('Perdita nella finestra')}</span>
          <strong className={`mono${persi > 0 ? ' eagle-motivo' : ''}`}>{(100 * persi / inviati).toLocaleString(localeCorrente(), { maximumFractionDigits: 2 })} %</strong>
          <span className="muted eagle-piccolo"> · {t('{n} ping', { n: inviati.toLocaleString(localeCorrente()) })}</span></div>
      )}
    </div>
  )
}

/** La sonda di cui mostrare le derivate: la principale se ha letture, altrimenti quella con più punti. */
function sondaDiRiferimento(punti: EaglePunto[], principale?: string | null): string | null {
  const conteggi = new Map<string, number>()
  for (const p of punti) if (p.appliance_id) conteggi.set(p.appliance_id, (conteggi.get(p.appliance_id) ?? 0) + 1)
  if (conteggi.size === 0) return null
  if (principale && conteggi.has(principale)) return principale
  return [...conteggi.entries()].sort((a, b) => b[1] - a[1])[0][0]
}
